import { joinURL, hasProtocol, withoutTrailingSlash } from 'ufo';
import { toHead, type ToMetaTagsType } from 'vue-datocms';

type TranslatedSlug = {
  locale?: string | null;
  value?: string | null;
};

export function useSeo(
  datoMetaTags: {
    attributes?: any;
    content?: string | null | undefined;
    tag: string;
  }[],
  page?: {
    __typename?: string;
    allTranslatedSlugs?: TranslatedSlug[] | null;
  },
) {
  const datoHead = toHead(datoMetaTags as ToMetaTagsType);
  const i18nHead = useLocaleHead({
    addDirAttribute: true,
    identifierAttribute: 'id',
    addSeoAttributes: true,
  });
  const localePath = useLocalePath();
  const { locales } = useI18n();
  const i18nConfig = useRuntimeConfig().public.i18n;
  const defaultLocale = String(i18nConfig.defaultLocale || 'de');
  const baseUrl = withoutTrailingSlash(String(i18nConfig.baseUrl ?? ''));

  const alternateLinks = buildAlternateLinks({
    translations: page?.allTranslatedSlugs ?? [],
    pageType: page?.__typename ?? '',
    localePath,
    locales: (unref(locales) ?? []) as { code?: string; iso?: string }[],
    defaultLocale,
    baseUrl,
  });

  const i18nLinks = (i18nHead.value.link || []).filter(
    (link) => !(link.rel === 'alternate' && link.hreflang),
  );

  useHead({
    htmlAttrs: {
      lang: i18nHead.value.htmlAttrs!.lang,
    },
    title: datoHead.title ?? '',
    titleTemplate: '%s | m&m public design',
    link: [...i18nLinks, ...alternateLinks, ...datoHead.link],
    meta: [...(i18nHead.value.meta || []), ...datoHead.meta],
  });
}

function buildAlternateLinks({
  translations,
  pageType,
  localePath,
  locales,
  defaultLocale,
  baseUrl,
}: {
  translations: TranslatedSlug[];
  pageType: string;
  localePath: (route: string | { name: string; params?: Record<string, string> }, locale?: string) => string;
  locales: { code?: string; iso?: string }[];
  defaultLocale: string;
  baseUrl: string;
}) {
  const links: {
    rel: string;
    href: string;
    hreflang: string;
    id: string;
  }[] = [];
  let defaultHref = '';

  for (const translation of translations) {
    if (!translation.locale || !translation.value) {
      continue;
    }

    const code = translation.locale.substring(0, 2);
    const path = resolveTranslatedPath(
      localePath,
      translation.value,
      pageType,
      code,
    );
    const href = toAbsoluteUrl(baseUrl, path);
    if (!href) {
      continue;
    }

    const iso = locales.find((locale) => locale.code === code)?.iso ?? code;

    links.push({
      rel: 'alternate',
      href,
      hreflang: code,
      id: `i18n-alt-${code}`,
    });

    if (iso !== code) {
      links.push({
        rel: 'alternate',
        href,
        hreflang: iso,
        id: `i18n-alt-${iso}`,
      });
    }

    if (code === defaultLocale) {
      defaultHref = href;
    }
  }

  if (defaultHref) {
    links.push({
      rel: 'alternate',
      href: defaultHref,
      hreflang: 'x-default',
      id: 'i18n-xd',
    });
  }

  return links;
}

function resolveTranslatedPath(
  localePath: (route: string | { name: string; params?: Record<string, string> }, locale?: string) => string,
  slug: string,
  pageType: string,
  locale: string,
) {
  if (!slug || slug === 'home') {
    return localePath('index', locale);
  }

  const routeName =
    pageType === 'JobPostRecord'
      ? 'jobs-slug'
      : pageType === 'ProjectRecord'
        ? 'projects-slug'
        : 'slug';

  return localePath({ name: routeName, params: { slug } }, locale);
}

function toAbsoluteUrl(baseUrl: string, path: string) {
  if (!path || hasProtocol(path)) {
    return path || baseUrl;
  }

  if (path === '/') {
    return baseUrl || '/';
  }

  return joinURL(baseUrl || '/', path);
}
